import React, { MouseEvent, useState, useEffect, useRef } from 'react';
import {
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
} from '@material-ui/core';
import {
  AttachFile as AttachIcon,
  CloudDownload as DownloadIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import colors from '../../theme/colors';

export interface Attachment {
  id?: string;
  filename?: string;
  modified?: string | undefined;
  created?: string;
  preview_url?: string;
  download_url?: string;
}

interface Props {
  attachments?: Attachment[];
  error?: boolean;
  fetchAttachments?: () => Promise<any>;
  entityId?: string;
}

// Define the keyframes for the spin animation
const spinAnimation = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Add the keyframes to the document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = spinAnimation;
  document.head.appendChild(style);
}

const AttachmentButton: React.FC<Props> = ({
  attachments = [],
  error = false,
  fetchAttachments,
}) => {

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [localAttachments, setLocalAttachments] =
    useState<Attachment[]>(attachments);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (attachments && attachments.length > 0) {
      const validAttachments = attachments.filter(
        (attachment) =>
          attachment && (attachment.download_url || attachment.preview_url)
      );
      setLocalAttachments(validAttachments);
    }
  }, [attachments]);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setAnchorEl(event.currentTarget);

    const hasValidAttachments =
      localAttachments &&
      localAttachments.length > 0 &&
      localAttachments.some(
        (att) => att && (att.filename || att.download_url || att.preview_url)
      );

    if (hasValidAttachments) {
      return;
    }

    if (fetchAttachments && !isFetching) {
      setIsFetching(true);
      fetchAttachments()
        .then((result) => {
          if (result && Array.isArray(result)) {
            const validAttachments = result.filter(
              (attachment) =>
                attachment &&
                (attachment.filename ||
                  attachment.download_url ||
                  attachment.preview_url)
            );
            setLocalAttachments(validAttachments);
          } else if (result && typeof result === 'object') {
            if (result.download_url || result.preview_url) {
              setLocalAttachments([result]);
            } else {
              setLocalAttachments([]);
            }
          }
        })
        .catch((err) => {
          console.error('Failed to fetch attachments:', err);
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (error) {
    return (
      <IconButton aria-label="Attachments" aria-haspopup="true">
        <FontAwesomeIcon color={colors.danger} icon={faTimes} />
      </IconButton>
    );
  }

  if (isFetching) {
    return (
      <IconButton aria-label="Attachments" aria-haspopup="true" disabled>
        <AttachIcon
          style={{ color: colors.green, animation: 'spin 1s linear infinite' }}
        />
      </IconButton>
    );
  }

  return (
    <React.Fragment>
      <IconButton
        buttonRef={buttonRef}
        aria-label="Attachments"
        aria-owns={open ? 'simple-popper' : undefined}
        aria-haspopup="true"
        onClick={handleButtonClick}
      >
        <AttachIcon />
      </IconButton>
      {localAttachments && localAttachments.length > 0 && (
        <Popover
          id="simple-popper"
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <List>
            {localAttachments.map(
              (attachment): JSX.Element => (
                <ListItem
                  key={
                    attachment.id ||
                    attachment.download_url ||
                    Math.random().toString()
                  }
                  style={{ margin: 10 }}
                >
                  <div style={{ paddingRight: '64px' }}>
                    <ListItemText
                      primary={attachment.filename}
                      secondary={
                        attachment.modified
                          ? attachment.modified
                          : attachment.created
                      }
                    />
                  </div>
                  <ListItemSecondaryAction>
                    {attachment.preview_url && (
                      <a
                        href={attachment.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconButton aria-label="View">
                          <ViewIcon />
                        </IconButton>
                      </a>
                    )}
                    {attachment.download_url && (
                      <a
                        href={attachment.download_url}
                        download={attachment.filename}
                      >
                        <IconButton aria-label="Download">
                          <DownloadIcon />
                        </IconButton>
                      </a>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              )
            )}
          </List>
        </Popover>
      )}
    </React.Fragment>
  );
};

export default AttachmentButton;
